import { Visibility, VisibilityOff, CalendarToday } from "@mui/icons-material";
import {
  IconButton,
  InputAdornment,
  inputBaseClasses,
  TextField,
} from "@mui/material";
import { useField } from "formik";
import PropTypes from "prop-types";
import { useState, useRef } from "react";

const CustomTextField = ({
  label,
  select = false,
  children,
  colorScheme = "white",
  // Se elimina la prop showErrorOnChange para usar el comportamiento por defecto (mostrar error cuando se toca el campo)
  ...props
}) => {
  const [field, meta] = useField(props);
  const inputRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };

  const contrastColor = colorScheme === "black" ? "text.dark" : "white";
  const dateIconColor = colorScheme === "white" ? "#fff" : contrastColor;
  const inputType =
    props.type === "password"
      ? showPassword
        ? "text"
        : "password"
      : props.type;

  let slotProps = { input: {} };

  if (props.type === "password") {
    slotProps.input.endAdornment = (
      <InputAdornment
        position="start"
        sx={{
          color: contrastColor,
          "& .MuiTypography-root": { color: contrastColor },
        }}
      >
        <IconButton
          aria-label={
            showPassword ? "Ocultar la contraseña" : "Mostrar la contraseña"
          }
          onClick={handleClickShowPassword}
          onMouseDown={handleMouseDownPassword}
          onMouseUp={handleMouseUpPassword}
          edge="end"
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    );
  } else if (props.type === "date") {
    slotProps.input.endAdornment = (
      <InputAdornment
        position="end"
        onClick={() => {
          if (inputRef.current) {
            if (typeof inputRef.current.showPicker === "function") {
              inputRef.current.showPicker();
            } else {
              inputRef.current.focus();
            }
          }
        }}
        sx={{ cursor: "pointer" }}
      >
        <CalendarToday sx={{ color: dateIconColor }} />
      </InputAdornment>
    );
  }

  // Se muestran los errores únicamente si el campo ya fue tocado.
  const errorToShow = meta.touched && Boolean(meta.error);
  const helperTextToShow = meta.touched && meta.error;

  return (
    <TextField
      label={label}
      select={select}
      {...field}
      {...props}
      type={inputType}
      inputRef={props.type === "date" ? inputRef : undefined}
      slotProps={slotProps}
      error={errorToShow}
      helperText={helperTextToShow}
      fullWidth
      color={contrastColor}
      sx={{
        "& .MuiFormHelperText-root": {
          fontSize: "14px",
          margin: 0,
          marginTop: 0.5,
        },
        "& .MuiInput-underline.Mui-focused:before": {
          borderBottomColor: contrastColor,
        },
        "& .MuiInput-underline:hover:before": {
          borderBottomColor: contrastColor,
        },
        "& .MuiInput-underline:after": {
          borderBottomColor: contrastColor,
        },
        "& .MuiInputBase-input.Mui-disabled": {
          "-webkit-text-fill-color": contrastColor,
          opacity: 0.7,
        },
        "& .MuiInput-underline.Mui-disabled:before": {
          borderBottomColor: contrastColor,
          borderBottomStyle: "solid",
        },
        [`[data-shrink=true]~ .${inputBaseClasses.root} > &`]: {
          opacity: 1,
          color: contrastColor,
        },
        "& .MuiFormLabel-root": {
          color: contrastColor,
          "&.Mui-focused": {
            color: contrastColor,
          },
          "&.Mui-disabled": {
            color: contrastColor,
          },
        },
        "& .MuiSvgIcon-root": {
          color: contrastColor,
        },
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: contrastColor,
          },
          "&:hover fieldset": {
            borderColor: contrastColor,
          },
          "&.Mui-focused fieldset": {
            borderColor: contrastColor,
          },
        },
        "& .MuiInputBase-input": {
          color: contrastColor,
        },
      }}
      margin="normal"
    >
      {select && children}
    </TextField>
  );
};

CustomTextField.propTypes = {
  label: PropTypes.string.isRequired,
  select: PropTypes.bool,
  children: PropTypes.node,
  type: PropTypes.string,
  colorScheme: PropTypes.oneOf(["white", "black"]),
};

export default CustomTextField;
